import { defineStore } from 'pinia'
import { useI18n } from 'vue-i18n'
import xor from 'lodash/xor'

/**
 * Favorite events state. Uses `window.sessionStorage` directly (client-only) rather than through a
 * composable.
 */
export const useFavoritesStore = defineStore('favorites', () => {
  const bridgeFetch = useBridgeFetch()
  const { locale } = useI18n()

  const eventIConcertIds = ref<number[]>([]) // Favorite event ids
  const eventUris = ref<string[]>([]) // Favorite event uris (populated from the bridge)
  const user = ref<any>(undefined) // Logged in user information
  const overlayIsOpen = ref(false)

  const enabled = computed(() => !!(user.value?.id && user.value?.hash))
  const debug = computed(() => user.value)

  function reset() {
    eventIConcertIds.value = []
    eventUris.value = []
  }
  function toggleEventIConcertId(eventIConcertId: number) {
    eventIConcertIds.value = xor(eventIConcertIds.value, [eventIConcertId])
    save({ user: user.value, eventIConcertIds: eventIConcertIds.value })
  }
  function openOverlayAndStoreId(eventIConcertId?: number) {
    overlayIsOpen.value = true
    if (eventIConcertId) {
      window.sessionStorage.setItem('eventIConcertId', String(eventIConcertId))
    }
  }
  function closeOverlay() {
    overlayIsOpen.value = false
    window.sessionStorage.removeItem('eventIConcertId')
  }
  function setUser(newUser: any) {
    user.value = newUser
    load({ user: newUser })
  }
  function updateFromBridge({ eventIConcertIds: ids, eventUris: uris }: { eventIConcertIds: number[]; eventUris: string[] }) {
    eventIConcertIds.value = ids
    eventUris.value = uris
  }
  function updateFromSession(eventIConcertId: number) {
    if (eventIConcertId && !eventIConcertIds.value.includes(eventIConcertId)) {
      eventIConcertIds.value = xor(eventIConcertIds.value, [eventIConcertId])
      save({ user: user.value, eventIConcertIds: eventIConcertIds.value })
    }
  }

  async function load({ user: currentUser }: { user: any }) {
    if (currentUser?.id && currentUser?.hash) {
      updateFromBridge(await bridgeFetch(`/favorites/v1/${locale.value}/${currentUser.id}/load.json?hash=${currentUser.hash}`))

      const storedIConcertId = sessionStorage.getItem('eventIConcertId') ? Number(sessionStorage.getItem('eventIConcertId')) : undefined
      if (storedIConcertId) {
        updateFromSession(storedIConcertId)
        window.sessionStorage.removeItem('eventIConcertId')
      }
    } else {
      reset()
    }
  }
  async function save({ user: currentUser, eventIConcertIds: ids }: { user: any; eventIConcertIds: number[] }) {
    if (currentUser?.id && currentUser?.hash) {
      updateFromBridge(
        await bridgeFetch(`/favorites/v1/${locale.value}/${currentUser.id}/save.json?hash=${currentUser.hash}`, {
          method: 'PUT',
          headers: { Accept: 'application/json' },
          body: JSON.stringify({ eventIConcertIds: ids }),
        }),
      )
    } else {
      reset()
    }
  }

  return {
    eventIConcertIds,
    eventUris,
    user,
    overlayIsOpen,
    enabled,
    debug,
    reset,
    toggleEventIConcertId,
    openOverlayAndStoreId,
    closeOverlay,
    setUser,
    updateFromBridge,
    updateFromSession,
    load,
    save,
  }
})
